import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { TrendingUp, DollarSign, Activity, BarChart3, LineChart as LineIcon, PieChart as PieIcon, AreaChart as AreaIcon, Calendar } from 'lucide-react';

const COLORS = ['#FF007F', '#8A2BE2', '#00F0FF', '#FF6B9D', '#B24BF3', '#5FF5FF'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-card p-3 rounded-xl border border-white/10">
        <p className="text-white text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AnalyticsModule = ({ providerId = null, isAdmin = false }) => {
  const [chartType, setChartType] = useState('line');
  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(providerId || 'all');
  const [providers, setProviders] = useState([]);
  const [overviewData, setOverviewData] = useState([]);
  const [byProviderData, setByProviderData] = useState([]);
  const [byServiceTypeData, setByServiceTypeData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);

    if (isAdmin) {
      loadProviders();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (startDate && endDate) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, selectedProvider]);

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API}/providers?approved=true`, { withCredentials: true });
      setProviders(res.data);
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [overviewRes, summaryRes, serviceTypeRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/analytics/summary?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/analytics/by-service-type?${params.toString()}`, { withCredentials: true })
      ]);

      setOverviewData(overviewRes.data);
      setSummary(summaryRes.data);
      setByServiceTypeData(serviceTypeRes.data);

      if (isAdmin) {
        const providerParams = new URLSearchParams();
        if (startDate) providerParams.append('start_date', startDate);
        if (endDate) providerParams.append('end_date', endDate);
        const providerRes = await axios.get(`${API}/analytics/by-provider?${providerParams.toString()}`, { withCredentials: true });
        setByProviderData(providerRes.data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const setQuickRange = (rangeType) => {
    const end = new Date();
    const start = new Date();
    switch (rangeType) {
      case 'today':
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        start.setDate(start.getDate() - 30);
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const renderChart = () => {
    if (overviewData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Activity className="w-16 h-16 text-[#9CA3AF] mb-4" />
          <p className="text-[#9CA3AF] mb-2">No hay datos para mostrar</p>
          <p className="text-sm text-[#6B7280]">Ajusta los filtros o registra servicios</p>
        </div>
      );
    }

    const commonProps = {
      data: overviewData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 }
    };

    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="period" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#9CA3AF' }} />
            <Line type="monotone" dataKey="total_services" stroke="#FF007F" strokeWidth={3} name="Servicios" dot={{ fill: '#FF007F', r: 4 }} />
            <Line type="monotone" dataKey="total_commission" stroke="#00F0FF" strokeWidth={3} name="Comisiones ($)" dot={{ fill: '#00F0FF', r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart {...commonProps}>
            <defs>
              <linearGradient id="barGradientPink" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF007F" stopOpacity={1} />
                <stop offset="100%" stopColor="#8A2BE2" stopOpacity={0.6} />
              </linearGradient>
              <linearGradient id="barGradientCyan" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00F0FF" stopOpacity={1} />
                <stop offset="100%" stopColor="#8A2BE2" stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="period" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#9CA3AF' }} />
            <Bar dataKey="total_services" fill="url(#barGradientPink)" name="Servicios" radius={[8, 8, 0, 0]} />
            <Bar dataKey="total_commission" fill="url(#barGradientCyan)" name="Comisiones ($)" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="areaGradientPink" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF007F" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#FF007F" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="areaGradientCyan" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#00F0FF" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="period" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#9CA3AF' }} />
            <Area type="monotone" dataKey="total_services" stroke="#FF007F" fill="url(#areaGradientPink)" strokeWidth={2} name="Servicios" />
            <Area type="monotone" dataKey="total_commission" stroke="#00F0FF" fill="url(#areaGradientCyan)" strokeWidth={2} name="Comisiones ($)" />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'pie') {
      // Aggregate all data into a single pie showing total services vs total commissions
      const pieData = overviewData.map((item) => ({
        name: item.period,
        value: item.total_services
      }));
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
              outerRadius={140}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
              stroke="rgba(255,255,255,0.1)"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#9CA3AF' }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card p-6 rounded-2xl" data-testid="analytics-summary-services">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-[#FF007F]" />
              <span className="text-xs text-[#9CA3AF] uppercase tracking-widest">Total Servicios</span>
            </div>
            <p className="text-3xl font-semibold text-white">{summary.total_services || 0}</p>
            <p className="text-xs text-[#6B7280] mt-2">Promedio: ${(summary.avg_service_amount || 0).toFixed(2)}</p>
          </div>

          <div className="glass-card p-6 rounded-2xl" data-testid="analytics-summary-amount">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-[#8A2BE2]" />
              <span className="text-xs text-[#9CA3AF] uppercase tracking-widest">Ingresos</span>
            </div>
            <p className="text-3xl font-semibold text-white">${(summary.total_amount || 0).toLocaleString()}</p>
            <p className="text-xs text-[#6B7280] mt-2">En el período seleccionado</p>
          </div>

          <div className="glass-card p-6 rounded-2xl" data-testid="analytics-summary-commission">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-[#00F0FF]" />
              <span className="text-xs text-[#9CA3AF] uppercase tracking-widest">Comisiones</span>
            </div>
            <p className="text-3xl font-semibold text-white">${(summary.total_commission || 0).toLocaleString()}</p>
            <p className="text-xs text-[#6B7280] mt-2">Promedio: ${(summary.avg_commission || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      <div className="glass-card p-6 rounded-2xl">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-[#8A2BE2]" />
          <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit' }}>Filtros</h3>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Button data-testid="quick-range-today" onClick={() => setQuickRange('today')} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5">Hoy</Button>
          <Button data-testid="quick-range-week" onClick={() => setQuickRange('week')} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5">Última Semana</Button>
          <Button data-testid="quick-range-month" onClick={() => setQuickRange('month')} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5">Último Mes</Button>
          <Button data-testid="quick-range-year" onClick={() => setQuickRange('year')} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5">Último Año</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-white text-sm mb-2 block">Desde</Label>
            <Input
              data-testid="filter-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-white/5 border-white/10 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-white text-sm mb-2 block">Hasta</Label>
            <Input
              data-testid="filter-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-white/5 border-white/10 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-white text-sm mb-2 block">Agrupar por</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="filter-period" className="bg-white/5 border-white/10 text-white rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1b1f] border-white/10 text-white">
                <SelectItem value="day">Día</SelectItem>
                <SelectItem value="week">Semana</SelectItem>
                <SelectItem value="month">Mes</SelectItem>
                <SelectItem value="year">Año</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div>
              <Label className="text-white text-sm mb-2 block">Proveedor</Label>
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger data-testid="filter-provider" className="bg-white/5 border-white/10 text-white rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1b1f] border-white/10 text-white">
                  <SelectItem value="all">Todos</SelectItem>
                  {providers.map(p => (
                    <SelectItem key={p.id || p.email} value={p.id || p.email}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="glass-card p-6 rounded-2xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            Servicios y Comisiones
          </h3>
          <div className="flex gap-2 p-1 bg-white/5 rounded-xl border border-white/10">
            <button
              data-testid="chart-type-line"
              onClick={() => setChartType('line')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                chartType === 'line' ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white' : 'text-[#9CA3AF] hover:text-white'
              }`}
            >
              <LineIcon className="w-4 h-4" />
              Líneas
            </button>
            <button
              data-testid="chart-type-bar"
              onClick={() => setChartType('bar')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                chartType === 'bar' ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white' : 'text-[#9CA3AF] hover:text-white'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Barras
            </button>
            <button
              data-testid="chart-type-area"
              onClick={() => setChartType('area')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                chartType === 'area' ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white' : 'text-[#9CA3AF] hover:text-white'
              }`}
            >
              <AreaIcon className="w-4 h-4" />
              Área
            </button>
            <button
              data-testid="chart-type-pie"
              onClick={() => setChartType('pie')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                chartType === 'pie' ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white' : 'text-[#9CA3AF] hover:text-white'
              }`}
            >
              <PieIcon className="w-4 h-4" />
              Circular
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-[#9CA3AF]">Cargando datos...</div>
          </div>
        ) : (
          renderChart()
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6 rounded-2xl">
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="w-5 h-5 text-[#00F0FF]" />
            <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit' }}>
              Distribución por Tipo de Servicio
            </h3>
          </div>
          {byServiceTypeData.length === 0 ? (
            <p className="text-center text-[#9CA3AF] py-16">Sin datos disponibles</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={byServiceTypeData}
                  dataKey="total_services"
                  nameKey="service_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ service_type, percent }) => `${service_type} ${(percent * 100).toFixed(0)}%`}
                >
                  {byServiceTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {isAdmin ? (
          <div className="glass-card p-6 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#FF007F]" />
              <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                Top Proveedores por Comisiones
              </h3>
            </div>
            {byProviderData.length === 0 ? (
              <p className="text-center text-[#9CA3AF] py-16">Sin datos disponibles</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byProviderData.slice(0, 5)} layout="vertical" margin={{ left: 20 }}>
                  <defs>
                    <linearGradient id="topProviderGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#FF007F" stopOpacity={1} />
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                  <YAxis type="category" dataKey="provider_name" stroke="#9CA3AF" style={{ fontSize: '11px' }} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total_commission" fill="url(#topProviderGradient)" name="Comisiones ($)" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        ) : (
          byServiceTypeData.length > 0 && (
            <div className="glass-card p-6 rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-[#FF007F]" />
                <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                  Comisiones por Servicio
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byServiceTypeData}>
                  <defs>
                    <linearGradient id="serviceCommissionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity={1} />
                      <stop offset="100%" stopColor="#8A2BE2" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="service_type" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total_commission" fill="url(#serviceCommissionGradient)" name="Comisiones ($)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AnalyticsModule;
